import { Check, CircleAlert, Copy, FlaskConical, Lock, PackagePlus, RotateCcw, Save, Settings2, Trash2, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, cn, ConfirmDialog, Dialog, EmptyState, ErrorState, Input, Panel, plural, Pressable, Segmented, Select, Switch, Textarea, toast, Tooltip, toUiError } from '@orgus/ui';
import { DISCIPLINE_GROUP_LABEL, disciplineRuleInfo, parsePackRef } from '@orgus/protocol';
import type { DisciplineDryRunResult, DisciplinePack, DisciplineRuleGroup, DisciplineState } from '@orgus/protocol';
import { useDisciplineActions } from './actions.ts';
import { CURATED, effectiveSettings, modelFromRaw, packChoices, packFromModel, setSetting, toYaml, togglePack } from './editor.ts';
import type { EditorModel } from './editor.ts';
import type { RepoRow } from './model.ts';

const KIND_LABEL: Record<DisciplinePack['kind'], string> = { base: 'Base', focus: 'Focus', language: 'Language', org: 'Your organisation' };

const packName = (p: DisciplinePack) => (p.id.startsWith('orgus/') ? p.title : `${p.id}@${p.version}`);

function PackChip({ p, on, onToggle, disabled }: { p: DisciplinePack; on: boolean; onToggle: () => void; disabled: boolean }) {
  return (
    <span className="inline-flex">
    <Pressable
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={on}
      title={p.description}
      className={cn('inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm', on ? 'border-accent bg-accent-soft text-fg' : 'border-border text-fg-muted hover:bg-hover', disabled && 'opacity-60')}
    >
      {on ? <Check className="size-3.5 text-accent" aria-hidden /> : <span className="size-3.5" aria-hidden />}
      {packName(p)}
    </Pressable>
    </span>
  );
}

function DryRunResult({ r }: { r: DisciplineDryRunResult }) {
  const [open, setOpen] = useState<string | null>(null);
  const delta = r.score - r.currentScore;
  return (
    <Panel title="Dry run" sub={`${plural(r.rules.filter((x) => x.files).length, 'rule')} would fail today`} icon={<FlaskConical />} flush>
      <div className="flex flex-wrap items-center gap-4 border-b border-border-subtle px-4 py-3">
        <span className="text-sm text-fg-muted">
          Score <span className="num font-semibold text-fg">{r.currentScore}</span> now, <span className="num font-semibold text-fg">{r.score}</span> with these rules
        </span>
        <Badge tone={delta < 0 ? 'failed' : delta > 0 ? 'passed' : 'neutral'}>{delta === 0 ? 'No change' : `${delta > 0 ? '+' : ''}${delta}`}</Badge>
        <span className="text-xs text-fg-subtle">Nothing was saved; the repo was only read.</span>
      </div>
      {r.problems.length > 0 && (
        <ul className="flex flex-col gap-1 border-b border-border-subtle px-4 py-2">
          {r.problems.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-waiting">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {p}
            </li>
          ))}
        </ul>
      )}
      <ul className="divide-y divide-border-subtle">
        {r.rules.map((x) => {
          const info = disciplineRuleInfo(x.rule);
          const change = x.files - x.currentFiles;
          return (
            <li key={x.rule}>
              <Pressable onClick={() => setOpen(open === x.rule ? null : x.rule)} aria-expanded={open === x.rule} className="flex items-center gap-3 px-4 py-2 hover:bg-hover">
                {x.files ? <CircleAlert className={cn('size-4 shrink-0', x.errors ? 'text-failed' : 'text-waiting')} aria-hidden /> : <Check className="size-4 shrink-0 text-passed" aria-hidden />}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-fg">{info.title}</span>
                  <span className="truncate text-sm text-fg-subtle">{x.files ? `This rule would fail ${plural(x.files, 'file')} today` : 'Passes today'}</span>
                </span>
                {change !== 0 && (
                  <Badge size="sm" tone={change > 0 ? 'failed' : 'passed'} variant="outline">
                    {change > 0 ? `+${change}` : change} vs now
                  </Badge>
                )}
              </Pressable>
              {open === x.rule && x.sample.length > 0 && (
                <ul className="flex flex-col gap-0.5 pb-2 pl-11 pr-4">
                  {x.sample.map((s) => (
                    <li key={s} className="truncate font-mono text-xs text-fg-muted">
                      {s}
                    </li>
                  ))}
                  {x.files > x.sample.length && <li className="text-xs text-fg-subtle">and {x.files - x.sample.length} more</li>}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function PublishDialog({ model, onClose }: { model: EditorModel; onClose: () => void }) {
  const actions = useDisciplineActions();
  const [id, setId] = useState('acme/house-rules');
  const [version, setVersion] = useState('1.0.0');
  const [title, setTitle] = useState('House rules');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  // A refusal from the hub, said in the dialog: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const idOk = !!parsePackRef(id) && id.includes('/') && !id.startsWith('orgus/');
  const verOk = /^\d+\.\d+\.\d+$/.test(version);
  const submit = async () => {
    if (!idOk || !verOk || !description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const p = packFromModel(model, id.trim().toLowerCase(), version, title.trim() || id, description.trim());
      await actions.publishPack(p);
      toast.success('Pack published', `${p.id}@${p.version} can now be extended and pinned across repos.`);
      onClose();
    } catch (e) {
      setError(toUiError(e, { what: 'The pack was not published.', next: 'Check the name and version; published versions never change.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Publish as an organisation pack"
      description="These packs and settings become a versioned pack other repos can extend and you can pin across the fleet."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<PackagePlus />} onClick={() => void submit()} loading={busy} disabled={!idOk || !verOk || !description.trim()}>
            Publish pack
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Pack" value={id} onChange={(e) => setId(e.target.value)} error={idOk ? null : 'Use org/name; orgus/ is reserved.'} className="font-mono" />
        <Input label="Version" value={version} onChange={(e) => setVersion(e.target.value)} error={verOk ? null : 'Like 1.0.0'} className="font-mono" />
        <Input wrapperClassName="sm:col-span-2" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Textarea wrapperClassName="sm:col-span-2" label="What it holds repos to" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One sentence people read before they adopt it" />
        {error != null && <ErrorState variant="inline" error={error} className="sm:col-span-2" />}
      </div>
    </Dialog>
  );
}

const GROUPS = [...new Set(CURATED.map((c) => c.group))] as DisciplineRuleGroup[];

/**
 * The rule editor: packs and the switches people change most, the discipline.yaml they
 * make, and a dry run against the repo before anything is kept.
 */
export function RuleEditor({ rows, state, projectId, onProject }: { rows: RepoRow[]; state: DisciplineState | undefined; projectId: string | null; onProject: (id: string) => void }) {
  const actions = useDisciplineActions();
  const row = rows.find((r) => r.projectId === projectId) ?? rows[0];
  const managed = row ? state?.managed?.[row.projectId] : undefined;
  const report = row?.report ?? null;
  const initial = useMemo(() => modelFromRaw(report?.rulebookRaw ?? null), [report?.rulebookRaw]);
  const [model, setModel] = useState<EditorModel>(initial);
  const [raw, setRaw] = useState<string | null>(null);
  const [result, setResult] = useState<DisciplineDryRunResult | null>(null);
  const [busy, setBusy] = useState<'dry' | 'save' | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [dropping, setDropping] = useState(false);
  // What failed, said over the rulebook: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setModel(initial);
    setRaw(null);
    setResult(null);
  }, [initial, row?.projectId]);
  const orgPacks = state?.packs ?? [];
  const choices = useMemo(() => packChoices(orgPacks), [orgPacks]);
  const pins = state?.pins;
  const enforced = useMemo(() => (row ? (pins ?? []).filter((p) => p.enforce && (p.scope === 'all' || p.scope.includes(row.projectId))).map((p) => `${p.pack}@${p.version}`) : []), [pins, row]);
  const eff = useMemo(() => effectiveSettings(model, orgPacks, enforced), [model, orgPacks, enforced]);
  /** The chooser of every pack folds behind Change packs; at rest only the chosen ones show. */
  const [packsOpen, setPacksOpen] = useState(false);
  const hasBase = model.extends.some((r) => choices.find((c) => c.id === parsePackRef(r)?.id)?.kind === 'base');
  const isOn = (p: DisciplinePack) => model.extends.some((r) => parsePackRef(r)?.id === p.id) || (p.id === 'orgus/recommended' && !hasBase);
  const yaml = raw ?? toYaml(model);
  const dirty = raw !== null || JSON.stringify(model) !== JSON.stringify(initial);

  if (!row) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState icon={<FlaskConical />} title="No repo to edit rules for" description="Add a project first; its rulebook can then be edited and tried here." />
      </div>
    );
  }

  const source = managed ? 'Kept in Orgus' : report?.rulebook === 'repo' ? "The repo's .orgus/discipline.yaml" : report ? 'Built-in recommended pack (no rulebook yet)' : 'Waiting for the first check';
  const dry = async () => {
    setBusy('dry');
    setError(null);
    try {
      setResult(await actions.dryRun(row.projectId, yaml));
    } catch (e) {
      setError(toUiError(e, { what: 'The dry run did not finish.', next: 'Make sure the machine holding the repo is online, then try again.' }));
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const { problems } = await actions.saveRulebook(row.projectId, yaml);
      toast.success('Rulebook kept in Orgus', problems.length ? `${plural(problems.length, 'setting')} will be ignored; see the dry run.` : `${row.name} is checked with it from now on. Copy the YAML into .orgus/discipline.yaml to commit it.`);
    } catch (e) {
      setError(toUiError(e, { what: 'The rulebook was not saved.', next: 'Only an owner or admin can change the rules.' }));
    } finally {
      setBusy(null);
    }
  };
  const copy = async () => {
    setError(null);
    try {
      await navigator.clipboard.writeText(yaml);
      toast.success('Copied', 'Paste it into .orgus/discipline.yaml in the repo and commit it.');
    } catch {
      setError({ what: 'The rulebook was not copied.', why: 'The browser did not allow it.', next: 'Select the text and copy it by hand.' });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error != null && <ErrorState variant="inline" error={error} />}
      <Panel
        title="Rulebook"
        sub={<span className="hidden sm:inline">{source}</span>}
        icon={managed ? <Lock /> : <FlaskConical />}
        actions={<Select size="sm" aria-label="Repo" value={row.projectId} onValueChange={onProject} options={rows.map((r) => ({ value: r.projectId, label: r.name }))} />}
      >
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className={cn('flex min-w-0 flex-col gap-4', raw !== null && 'pointer-events-none opacity-50')} aria-disabled={raw !== null}>
            <section className="flex min-w-0 flex-col gap-2" aria-label="Packs">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h3 className="text-sm font-medium text-fg-muted">Packs</h3>
                {choices.filter(isOn).map((p) => (
                  <Badge key={p.id} data-chip="" size="sm" variant="outline" title={p.description}>
                    {packName(p)}
                  </Badge>
                ))}
                <Button size="sm" variant="ghost" icon={<Settings2 />} aria-expanded={packsOpen} onClick={() => setPacksOpen((o) => !o)}>
                  {packsOpen ? 'Hide packs' : 'Change packs'}
                </Button>
              </div>
              {packsOpen &&
                (['base', 'focus', 'language', 'org'] as const).map((kind) => {
                  const list = choices.filter((p) => p.kind === kind);
                  if (!list.length) return null;
                  return (
                    <div key={kind} className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="w-full shrink-0 text-xs text-fg-subtle sm:w-24">{KIND_LABEL[kind]}</span>
                      {list.map((p) => {
                        const on = isOn(p);
                        return <PackChip key={p.id} p={p} on={on} disabled={p.id === 'orgus/recommended'} onToggle={() => setModel((m) => togglePack(m, p.id, !on))} />;
                      })}
                    </div>
                  );
                })}
              {enforced.length > 0 && (
                <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
                  <Lock className="size-3 shrink-0" aria-hidden />
                  Enforced by your organisation on top of these rules: {enforced.join(', ')}. Change it under Rule packs below.
                </p>
              )}
            </section>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {GROUPS.map((g) => (
                <section key={g} className="flex min-w-0 flex-col" aria-label={DISCIPLINE_GROUP_LABEL[g]}>
                  <h3 className="pb-1 text-sm font-medium text-fg-muted">{DISCIPLINE_GROUP_LABEL[g]}</h3>
                  <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
                    {CURATED.filter((c) => c.group === g).map((c) => {
                      const k = `${c.section}.${c.key}`;
                      const e = eff[k]!;
                      const own = model.settings[c.section]?.[c.key] !== undefined;
                      return (
                        <li key={k} className="flex min-h-row-2line min-w-0 items-center gap-3 px-3 py-2">
                          <span className="flex min-w-0 flex-1 flex-col">
                            <Tooltip content={c.hint}>
                              <span tabIndex={-1} className="line-clamp-2 text-sm text-fg sm:truncate">
                                {c.label}
                                <span className="sr-only">: {c.hint}</span>
                              </span>
                            </Tooltip>
                            <span className={cn('truncate text-caption', own ? 'text-accent-text' : 'text-fg-subtle')}>{own ? 'Set here' : `From ${e.from.replace(/^orgus\//, '')}`}</span>
                          </span>
                          {c.kind === 'bool' ? (
                            <Switch checked={!!e.value} onCheckedChange={(v) => setModel((m) => setSetting(m, c.section, c.key, v === true))} aria-label={c.label} />
                          ) : (
                            <Input size="sm" type="number" min={0} aria-label={c.label} wrapperClassName="w-16 shrink-0" className="text-ui num" value={String(e.value)} onChange={(ev) => setModel((m) => setSetting(m, c.section, c.key, Math.max(0, Math.round(Number(ev.target.value) || 0))))} />
                          )}
                          {own && (
                            <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={() => setModel((m) => setSetting(m, c.section, c.key, undefined))} aria-label={`Let the packs decide ${c.label}`}>
                              <span className="sr-only">Reset</span>
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 lg:sticky lg:top-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-fg-muted">discipline.yaml</h3>
              <Segmented
                size="sm"
                label="Edit mode"
                value={raw === null ? 'visual' : 'yaml'}
                onChange={(v) => setRaw(v === 'yaml' ? toYaml(model) : null)}
                options={[
                  { value: 'visual', label: 'Visual' },
                  { value: 'yaml', label: 'YAML' },
                ]}
              />
            </div>
            <Textarea aria-label="discipline.yaml" rows={Math.min(16, Math.max(6, yaml.split('\n').length + 1))} value={yaml} readOnly={raw === null} onChange={(e) => setRaw(e.target.value)} className="font-mono text-xs" spellCheck={false} />
            {raw !== null && <p className="text-xs text-fg-subtle">Editing the text directly; the dry run checks it. Switch back to Visual to drop text edits.</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary" icon={<FlaskConical />} onClick={() => void dry()} loading={busy === 'dry'} disabled={!report || busy !== null}>
                Dry run
              </Button>
              <Button variant="secondary" icon={<Save />} onClick={() => void save()} loading={busy === 'save'} disabled={busy !== null || (!dirty && !!managed)}>
                Keep in Orgus
              </Button>
              <Button variant="ghost" icon={<Copy />} onClick={() => void copy()}>
                Copy YAML
              </Button>
              <Button variant="ghost" icon={<PackagePlus />} onClick={() => setPublishing(true)} disabled={raw !== null}>
                Publish as pack
              </Button>
              {managed && (
                <Button variant="ghost" icon={<Trash2 />} onClick={() => setDropping(true)}>
                  Stop keeping it
                </Button>
              )}
            </div>
            <p className="text-xs text-fg-subtle">Orgus never writes into the repo, so commit the YAML yourself.</p>
          </div>
        </div>
      </Panel>
      {result && <DryRunResult r={result} />}
      {publishing && <PublishDialog model={model} onClose={() => setPublishing(false)} />}
      {dropping && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setDropping(false)}
          title={`Stop keeping a rulebook for ${row.name}?`}
          description="The next check uses the repo's own .orgus/discipline.yaml, or the recommended pack when it has none."
          confirmLabel="Stop keeping it"
          onConfirm={async () => {
            await actions.dropRulebook(row.projectId);
            toast.success('Rulebook removed from Orgus', `${row.name} is checked with its own rules again.`);
            setDropping(false);
          }}
        />
      )}
    </div>
  );
}
